/**
 * OPS-MODULE-ADMIN-002a — module-centric user assignment registry (single source of truth).
 * Classifies tenant roles into operational modules; scopes dept-admin visibility.
 */

/** @typedef {'ADMINISTRATION'|'FRONT_OFFICE'|'BILLING'|'LABORATORY'|'RADIOLOGY'|'PHARMACY'|'CLINICAL'} ModuleKey */

export const MODULE_KEYS = Object.freeze({
  ADMINISTRATION: 'ADMINISTRATION',
  FRONT_OFFICE: 'FRONT_OFFICE',
  BILLING: 'BILLING',
  LABORATORY: 'LABORATORY',
  RADIOLOGY: 'RADIOLOGY',
  PHARMACY: 'PHARMACY',
  CLINICAL: 'CLINICAL',
});

/** Display order for overview cards. */
export const MODULE_ORDER = Object.freeze([
  MODULE_KEYS.ADMINISTRATION,
  MODULE_KEYS.FRONT_OFFICE,
  MODULE_KEYS.BILLING,
  MODULE_KEYS.LABORATORY,
  MODULE_KEYS.RADIOLOGY,
  MODULE_KEYS.PHARMACY,
  MODULE_KEYS.CLINICAL,
]);

export const MODULE_DEFINITIONS = Object.freeze({
  [MODULE_KEYS.ADMINISTRATION]: {
    key: MODULE_KEYS.ADMINISTRATION,
    label: 'Administration',
    description: 'Platform and hospital administration roles',
  },
  [MODULE_KEYS.FRONT_OFFICE]: {
    key: MODULE_KEYS.FRONT_OFFICE,
    label: 'Front Office',
    description: 'Registration, reception, and patient journey desk staff',
  },
  [MODULE_KEYS.BILLING]: {
    key: MODULE_KEYS.BILLING,
    label: 'Billing',
    description: 'Billing counter, cashier, and finance desk roles',
  },
  [MODULE_KEYS.LABORATORY]: {
    key: MODULE_KEYS.LABORATORY,
    label: 'Laboratory',
    description: 'Lab technicians, verifiers, and lab administrators',
  },
  [MODULE_KEYS.RADIOLOGY]: {
    key: MODULE_KEYS.RADIOLOGY,
    label: 'Radiology',
    description: 'Radiology technicians and imaging staff',
  },
  [MODULE_KEYS.PHARMACY]: {
    key: MODULE_KEYS.PHARMACY,
    label: 'Pharmacy',
    description: 'Pharmacists and pharmacy desk staff',
  },
  [MODULE_KEYS.CLINICAL]: {
    key: MODULE_KEYS.CLINICAL,
    label: 'Clinical',
    description: 'Non-provider clinical operations (not doctors)',
    doctorManagementNote:
      'Doctor accounts and DOCTOR role assignments are managed in Doctor Management, not here.',
  },
});

/** Hard denylist — never shown or assignable in module admin. */
export const EXCLUDED_MODULE_ADMIN_ROLE_IDS = Object.freeze([
  'DOCTOR',
  'PHYSICIAN',
  'SURGEON',
  'CONSULTANT',
  'SPECIALIST',
  'MEDICAL_OFFICER',
]);

/** Substring/prefix deny patterns (uppercase role id). */
export const EXCLUDED_MODULE_ADMIN_ROLE_PATTERNS = Object.freeze([
  /^DOC(?:TOR)?$/i,
  /_DOCTOR$/i,
  /^DOCTOR_/i,
  /PHYSICIAN/i,
  /SURGEON/i,
]);

/** Dept-admin actor role → module they may manage. ADMIN = all (null sentinel). */
export const ACTOR_MODULE_ADMIN_SCOPE = Object.freeze({
  ADMIN: null,
  HOSPITAL_ADMIN: null,
  SUPER_ADMIN: null,
  SYSTEM_ADMIN: null,
  PORTAL_ADMIN: null,
  LAB_ADMIN: MODULE_KEYS.LABORATORY,
  RADIOLOGY_ADMIN: MODULE_KEYS.RADIOLOGY,
  PHARMACY_ADMIN: MODULE_KEYS.PHARMACY,
  BILLING_ADMIN: MODULE_KEYS.BILLING,
  FRONT_OFFICE_ADMIN: MODULE_KEYS.FRONT_OFFICE,
});

/** Hospital-wide administrators — same module visibility as ADMIN. */
export const HOSPITAL_WIDE_ADMIN_ROLE_IDS = Object.freeze([
  'ADMIN',
  'HOSPITAL_ADMIN',
  'SUPER_ADMIN',
  'SYSTEM_ADMIN',
  'PORTAL_ADMIN',
]);

/** Platform roles. Visible only to an actor who already holds one of them. */
export const SYSTEM_DELEGATION_ROLE_IDS = Object.freeze([
  'SUPER_ADMIN',
  'SYSTEM_ADMIN',
  'PORTAL_ADMIN',
]);

/** Department administrators — only a hospital-wide admin may appoint them. */
export const DEPARTMENT_ADMIN_ROLE_IDS = Object.freeze([
  'LAB_ADMIN',
  'RADIOLOGY_ADMIN',
  'PHARMACY_ADMIN',
  'BILLING_ADMIN',
  'FRONT_OFFICE_ADMIN',
]);

/** Sensitive roles reserved for hospital-wide appointment (plus dept-admin roles). */
export const HOSPITAL_ONLY_ASSIGNMENT_ROLE_IDS = Object.freeze([
  'BILLING_CREDIT_APPROVER',
  ...DEPARTMENT_ADMIN_ROLE_IDS,
]);

/** Read-only module visibility (view members, no assign/remove). */
export const ACTOR_MODULE_READ_ONLY_SCOPE = Object.freeze({
  MEDICAL_SUPERINTENDENT: MODULE_KEYS.CLINICAL,
});

/** Explicit roleId → module (checked before prefix rules). */
const EXPLICIT_ROLE_MODULE = Object.freeze({
  ADMIN: MODULE_KEYS.ADMINISTRATION,
  HOSPITAL_ADMIN: MODULE_KEYS.ADMINISTRATION,
  SUPER_ADMIN: MODULE_KEYS.ADMINISTRATION,
  SYSTEM_ADMIN: MODULE_KEYS.ADMINISTRATION,
  PORTAL_ADMIN: MODULE_KEYS.ADMINISTRATION,
  OPS_ADMIN_SUPPORT: MODULE_KEYS.ADMINISTRATION,
  OPS_SUPERVISOR: MODULE_KEYS.ADMINISTRATION,
  RECEPTIONIST: MODULE_KEYS.FRONT_OFFICE,
  OPS_FLOOR_COORDINATOR: MODULE_KEYS.FRONT_OFFICE,
  FRONT_OFFICE_ADMIN: MODULE_KEYS.FRONT_OFFICE,
  OPS_CASHIER: MODULE_KEYS.BILLING,
  OPS_BILLING_SUPERVISOR: MODULE_KEYS.BILLING,
  OPS_DISCHARGE_DESK: MODULE_KEYS.BILLING,
  BILLING_ADMIN: MODULE_KEYS.BILLING,
  BILLING_CREDIT_APPROVER: MODULE_KEYS.BILLING,
  ACCOUNTANT: MODULE_KEYS.BILLING,
  LAB_ADMIN: MODULE_KEYS.LABORATORY,
  OPS_LAB_TECHNICIAN: MODULE_KEYS.LABORATORY,
  OPS_LAB_VERIFIER: MODULE_KEYS.LABORATORY,
  OPS_RAD_TECHNICIAN: MODULE_KEYS.RADIOLOGY,
  OPS_RADIOLOGIST: MODULE_KEYS.RADIOLOGY,
  RADIOLOGY_ADMIN: MODULE_KEYS.RADIOLOGY,
  OPS_PHARMACIST: MODULE_KEYS.PHARMACY,
  OPS_PHARMACY_SUPERVISOR: MODULE_KEYS.PHARMACY,
  PHARMACY_ADMIN: MODULE_KEYS.PHARMACY,
  MEDICAL_SUPERINTENDENT: MODULE_KEYS.CLINICAL,
  NURSE: MODULE_KEYS.CLINICAL,
  WARD_STAFF: MODULE_KEYS.CLINICAL,
  STAFF: MODULE_KEYS.ADMINISTRATION,
});

/** Prefix rules — first match wins (after explicit map). */
const PREFIX_MODULE_RULES = Object.freeze([
  { prefix: 'OPS_LAB_', module: MODULE_KEYS.LABORATORY },
  { prefix: 'OPS_RAD_', module: MODULE_KEYS.RADIOLOGY },
  { prefix: 'OPS_RADIOLOGIST', module: MODULE_KEYS.RADIOLOGY },
  { prefix: 'OPS_PHARMACY_', module: MODULE_KEYS.PHARMACY },
  { prefix: 'OPS_PHARMACIST', module: MODULE_KEYS.PHARMACY },
  { prefix: 'OPS_BILLING_', module: MODULE_KEYS.BILLING },
  { prefix: 'OPS_CASHIER', module: MODULE_KEYS.BILLING },
  { prefix: 'OPS_DISCHARGE_', module: MODULE_KEYS.BILLING },
  { prefix: 'OPS_FLOOR_', module: MODULE_KEYS.FRONT_OFFICE },
  { prefix: 'LAB_', module: MODULE_KEYS.LABORATORY },
  { prefix: 'RAD_', module: MODULE_KEYS.RADIOLOGY },
  { prefix: 'PHARMACY_', module: MODULE_KEYS.PHARMACY },
  { prefix: 'BILLING_', module: MODULE_KEYS.BILLING },
  { prefix: 'FRONT_OFFICE_', module: MODULE_KEYS.FRONT_OFFICE },
]);

/**
 * @param {string[]|string} roleList
 * @returns {string[]}
 */
export function normalizeActorRoles(roleList) {
  if (!roleList) {
    return [];
  }
  const raw = Array.isArray(roleList) ? roleList : [roleList];
  return raw.map((r) => String(r ?? '').trim().toUpperCase()).filter(Boolean);
}

export function hasHospitalWideModuleAdminAccess(actorRoles) {
  const roles = normalizeActorRoles(actorRoles);
  return roles.some((role) => HOSPITAL_WIDE_ADMIN_ROLE_IDS.includes(role));
}

/**
 * @param {string} roleId
 * @returns {boolean}
 */
export function isExcludedFromModuleAdmin(roleId) {
  const id = String(roleId ?? '').trim().toUpperCase();
  if (!id) {
    return true;
  }
  if (EXCLUDED_MODULE_ADMIN_ROLE_IDS.includes(id)) {
    return true;
  }
  return EXCLUDED_MODULE_ADMIN_ROLE_PATTERNS.some((pattern) => pattern.test(id));
}

export function isSystemDelegationRole(roleId) {
  const id = String(roleId ?? '').trim().toUpperCase();
  return SYSTEM_DELEGATION_ROLE_IDS.includes(id);
}

export function actorMayAssignSystemRole(actorRoles) {
  return normalizeActorRoles(actorRoles).some((role) => SYSTEM_DELEGATION_ROLE_IDS.includes(role));
}

/**
 * Backend assignability. Hospital administrators can assign operational roles
 * in their modules. They cannot assign system roles, and a pharmacy
 * administrator cannot assign roles outside pharmacy.
 */
export function isHospitalOnlyAssignmentRole(roleId) {
  const id = String(roleId ?? '').trim().toUpperCase();
  return HOSPITAL_ONLY_ASSIGNMENT_ROLE_IDS.includes(id);
}

export function canAssignRole(actorRoles, roleId) {
  if (isExcludedFromModuleAdmin(roleId)) {
    return false;
  }
  if (isSystemDelegationRole(roleId) && !actorMayAssignSystemRole(actorRoles)) {
    return false;
  }
  if (isHospitalOnlyAssignmentRole(roleId) && !hasHospitalWideModuleAdminAccess(actorRoles)) {
    return false;
  }
  const moduleKey = classifyRoleModule(roleId);
  if (!moduleKey) {
    return false;
  }
  return canActorAssignInModule(actorRoles, moduleKey);
}

export function canEditRoleGrants(actorRoles, roleId) {
  return canAssignRole(actorRoles, roleId);
}

/**
 * @param {string} roleId
 * @returns {ModuleKey|null} null if excluded or unclassified
 */
export function classifyRoleModule(roleId) {
  const id = String(roleId ?? '').trim().toUpperCase();
  if (!id || isExcludedFromModuleAdmin(id)) {
    return null;
  }
  if (EXPLICIT_ROLE_MODULE[id]) {
    return EXPLICIT_ROLE_MODULE[id];
  }
  for (const rule of PREFIX_MODULE_RULES) {
    if (id.startsWith(rule.prefix)) {
      return rule.module;
    }
  }
  return null;
}

/**
 * @param {Array<{ roleId: string, status?: string, roleDescription?: string }>} catalog
 * @param {ModuleKey} moduleKey
 * @returns {Array<{ roleId: string, status: string, roleDescription: string, moduleKey: ModuleKey }>}
 */
export function getModuleRoles(catalog, moduleKey) {
  return (catalog || [])
    .filter((row) => {
      if (isExcludedFromModuleAdmin(row.roleId)) {
        return false;
      }
      return classifyRoleModule(row.roleId) === moduleKey;
    })
    .map((row) => ({
      roleId: row.roleId,
      roleDescription: row.roleDescription || row.roleId,
      status: row.status || 'A',
      moduleKey,
    }));
}

/**
 * @param {string[]|string} actorRoles
 * @returns {ModuleKey[]}
 */
export function resolveVisibleModuleKeys(actorRoles) {
  const roles = normalizeActorRoles(actorRoles);
  // Hospital-wide admin wins over provider-role exclusion (multi-role union).
  if (hasHospitalWideModuleAdminAccess(roles)) {
    return [...MODULE_ORDER];
  }
  const visible = new Set();
  for (const actorRole of roles) {
    if (isExcludedFromModuleAdmin(actorRole)) {
      continue;
    }
    const moduleKey = ACTOR_MODULE_ADMIN_SCOPE[actorRole];
    if (moduleKey) {
      visible.add(moduleKey);
    }
    const readOnlyKey = ACTOR_MODULE_READ_ONLY_SCOPE[actorRole];
    if (readOnlyKey) {
      visible.add(readOnlyKey);
    }
  }
  return MODULE_ORDER.filter((key) => visible.has(key));
}

/**
 * @param {string[]|string} actorRoles
 * @returns {ModuleKey[]}
 */
export function resolveManageableModuleKeys(actorRoles) {
  const roles = normalizeActorRoles(actorRoles);
  if (hasHospitalWideModuleAdminAccess(roles)) {
    return [...MODULE_ORDER];
  }
  const manageable = new Set();
  for (const actorRole of roles) {
    if (isExcludedFromModuleAdmin(actorRole)) {
      continue;
    }
    const moduleKey = ACTOR_MODULE_ADMIN_SCOPE[actorRole];
    if (moduleKey) {
      manageable.add(moduleKey);
    }
  }
  return MODULE_ORDER.filter((key) => manageable.has(key));
}

/**
 * @param {string[]|string} actorRoles
 * @param {ModuleKey} moduleKey
 * @returns {boolean}
 */
export function canActorManageModule(actorRoles, moduleKey) {
  return resolveVisibleModuleKeys(actorRoles).includes(moduleKey);
}

/**
 * @param {string[]|string} actorRoles
 * @param {ModuleKey} moduleKey
 * @returns {boolean}
 */
export function canActorAssignInModule(actorRoles, moduleKey) {
  return resolveManageableModuleKeys(actorRoles).includes(moduleKey);
}

/**
 * @param {Array<{ roleId: string }>} catalog
 * @returns {Record<ModuleKey, string[]>}
 */
export function buildModuleRoleMappingReport(catalog) {
  /** @type {Record<string, string[]>} */
  const report = {};
  for (const key of MODULE_ORDER) {
    report[key] = getModuleRoles(catalog, key).map((r) => r.roleId).sort();
  }
  report.UNCLASSIFIED = (catalog || [])
    .map((r) => r.roleId)
    .filter((roleId) => !isExcludedFromModuleAdmin(roleId) && !classifyRoleModule(roleId))
    .sort();
  report.EXCLUDED = (catalog || [])
    .map((r) => r.roleId)
    .filter((roleId) => isExcludedFromModuleAdmin(roleId))
    .sort();
  return report;
}

/**
 * Static visibility matrix for documentation / tests.
 * @returns {Record<string, ModuleKey[]>}
 */
export function buildVisibleModuleMatrix() {
  /** @type {Record<string, ModuleKey[]>} */
  const matrix = {};
  matrix.ADMIN = [...MODULE_ORDER];
  matrix.HOSPITAL_ADMIN = [...MODULE_ORDER];
  matrix.SUPER_ADMIN = [...MODULE_ORDER];
  matrix.SYSTEM_ADMIN = [...MODULE_ORDER];
  matrix.PORTAL_ADMIN = [...MODULE_ORDER];
  for (const [actorRole, moduleKey] of Object.entries(ACTOR_MODULE_ADMIN_SCOPE)) {
    if (HOSPITAL_WIDE_ADMIN_ROLE_IDS.includes(actorRole) || !moduleKey) {
      continue;
    }
    matrix[actorRole] = [moduleKey];
  }
  for (const [actorRole, moduleKey] of Object.entries(ACTOR_MODULE_READ_ONLY_SCOPE)) {
    matrix[actorRole] = [moduleKey];
  }
  matrix.LAB_ADMIN_AND_BILLING_ADMIN = [
    MODULE_KEYS.LABORATORY,
    MODULE_KEYS.BILLING,
  ];
  matrix.OPS_LAB_TECHNICIAN = [];
  matrix.DOCTOR = [];
  return matrix;
}
